<template>
  <div class="property-detail-wrapper">
    <!-- Loading State -->
    <div v-if="!apartment" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Cargando información...</p>
    </div>

    <!-- Property Detail Content -->
    <div class="property-detail fade-in" v-else>
      <!-- Progress Indicator -->
      <BuyProgress
        v-if="customer != 'clab' && ['default', 'redondo'].includes(template)"
        :apartment="apartment"
        :country="country"
      />

      <!-- Photo Gallery -->
      <div class="gallery-section" v-if="photos && photos.length > 0">
        <div class="gallery-main" @click="showPhoto">
          <div
            class="gallery-image"
            :style="{
              backgroundImage:
                'url(' + getPhotoUrl(photos[carouselPosition]) + ')',
            }"
          >
            <div class="gallery-overlay">
              <div class="gallery-nav">
                <button
                  class="gallery-btn prev"
                  @click.stop="prevPhoto"
                  v-if="photos.length > 1"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M15 18L9 12L15 6"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
                <button
                  class="gallery-btn next"
                  @click.stop="nextPhoto"
                  v-if="photos.length > 1"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M9 18L15 12L9 6"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
              <div class="gallery-counter">
                {{ carouselPosition + 1 }} / {{ photos.length }}
              </div>
              <div class="gallery-hint">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 3H21V9"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M9 21H3V15"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M21 3L14 10"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M3 21L10 14"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span>Click para ampliar</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Photo Thumbnails -->
        <div class="gallery-thumbs" v-if="photos.length > 1">
          <button
            v-for="(photo, index) in photos.slice(0, 5)"
            :key="index"
            class="thumb-btn"
            :class="{ active: carouselPosition === index }"
            @click="carouselPosition = index"
            :style="{ backgroundImage: 'url(' + getPhotoUrl(photo) + ')' }"
          >
            <span v-if="index === 4 && photos.length > 5" class="thumb-more">
              +{{ photos.length - 5 }}
            </span>
          </button>
        </div>

        <!-- Fullscreen Modal -->
        <b-modal
          id="modal-photo"
          size="xl"
          centered
          hide-header
          body-bg-variant="dark"
          content-class="gallery-modal"
          hide-footer
        >
          <div class="modal-gallery">
            <img :src="getPhotoUrl(photos[setedPhoto])" alt="Foto propiedad" />
            <div class="modal-nav">
              <button
                class="modal-btn"
                @click="
                  setedPhoto = (setedPhoto - 1 + photos.length) % photos.length
                "
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <span class="modal-counter"
                >{{ setedPhoto + 1 }} / {{ photos.length }}</span
              >
              <button
                class="modal-btn"
                @click="setedPhoto = (setedPhoto + 1) % photos.length"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </b-modal>
      </div>

      <!-- Property Stats -->
      <div class="stats-section" v-if="!hideData(apartment.asset_type)">
        <div class="stats-grid">
          <div
            class="stat-card"
            v-if="apartment.bedrooms"
            style="--card-index: 0"
          >
            <div class="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V7"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
                <path d="M21 13H3" stroke="currentColor" stroke-width="1.5" />
                <path
                  d="M3 10C3 8.89543 3.89543 8 5 8H8C9.10457 8 10 8.89543 10 10V13H3V10Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                />
                <path
                  d="M14 10C14 8.89543 14.8954 8 16 8H19C20.1046 8 21 8.89543 21 10V13H14V10Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                />
              </svg>
            </div>
            <div class="stat-info">
              <span class="stat-value">{{ apartment.bedrooms }}</span>
              <span class="stat-label"
                >Dormitorio{{ apartment.bedrooms > 1 ? "s" : "" }}</span
              >
            </div>
          </div>

          <div
            class="stat-card"
            v-if="apartment.bathrooms"
            style="--card-index: 1"
          >
            <div class="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 12H20V16C20 18.2091 18.2091 20 16 20H8C5.79086 20 4 18.2091 4 16V12Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                />
                <path
                  d="M6 12V5C6 3.89543 6.89543 3 8 3H9C10.1046 3 11 3.89543 11 5V6"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
            </div>
            <div class="stat-info">
              <span class="stat-value">{{ apartment.bathrooms }}</span>
              <span class="stat-label"
                >Baño{{ apartment.bathrooms > 1 ? "s" : "" }}</span
              >
            </div>
          </div>

          <div
            class="stat-card"
            v-if="apartment.parking_lots"
            style="--card-index: 2"
          >
            <div class="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect
                  x="3"
                  y="8"
                  width="18"
                  height="12"
                  rx="2"
                  stroke="currentColor"
                  stroke-width="1.5"
                />
                <path
                  d="M7 15H9"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
                <path
                  d="M15 15H17"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
                <path
                  d="M5 8L7 4H17L19 8"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div class="stat-info">
              <span class="stat-value">{{ apartment.parking_lots }}</span>
              <span class="stat-label"
                >Estacionamiento{{
                  apartment.parking_lots > 1 ? "s" : ""
                }}</span
              >
            </div>
          </div>

          <div class="stat-card" v-if="apartment.cellars">
            <div class="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 21H21"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
                <path
                  d="M5 21V7L12 3L19 7V21"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M9 21V15H15V21"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div class="stat-info">
              <span class="stat-value">{{ apartment.cellars }}</span>
              <span class="stat-label"
                >Bodega{{ apartment.cellars > 1 ? "s" : "" }}</span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Surface Areas -->
      <div class="areas-section content-section" style="--section-index: 1">
        <h3 class="section-title">Superficies</h3>
        <div class="areas-grid">
          <div
            class="area-card"
            v-if="
              country == 1 &&
              apartment.useful_surface &&
              !hideData(apartment.asset_type)
            "
          >
            <span class="area-value"
              >{{ formatNumber(apartment.useful_surface) }} m²*</span
            >
            <span class="area-label">Superficie Útil</span>
          </div>
          <div
            class="area-card"
            v-if="
              country == 0 &&
              apartment.covered_surface &&
              !hideData(apartment.asset_type)
            "
          >
            <span class="area-value"
              >{{ formatNumber(apartment.covered_surface) }} m²*</span
            >
            <span class="area-label">Área Techada</span>
          </div>
          <div
            class="area-card"
            v-if="country == 1 && !hideData(apartment.asset_type)"
          >
            <span
              class="area-value"
              v-if="['CASA', 'LOTE'].includes(apartment.asset_type)"
              >{{ formatNumber(apartment.terrain_surface) }} m²*</span
            >
            <span class="area-value" v-else
              >{{ formatNumber(apartment.terrace_surface) }} m²*</span
            >
            <span
              class="area-label"
              v-if="['CASA', 'LOTE'].includes(apartment.asset_type)"
              >Superficie Terreno</span
            >
            <span class="area-label" v-else>Superficie Terraza</span>
          </div>
          <div
            class="area-card"
            v-if="
              country == 0 &&
              apartment.free_surface &&
              !hideData(apartment.asset_type)
            "
          >
            <span class="area-value"
              >{{ formatNumber(apartment.free_surface) }} m²*</span
            >
            <span class="area-label">Área Libre</span>
          </div>
          <div class="area-card" v-if="apartment.total_surface">
            <span class="area-value"
              >{{ formatNumber(apartment.total_surface) }} m²*</span
            >
            <span class="area-label">Superficie Total</span>
          </div>
        </div>
        <p
          class="full-width small"
          style="font-size: 0.7em; font-style: italic; margin-top: 10px"
        >
          *Tamaños aproximados
        </p>
      </div>
      <!-- Construction Progress -->
      <div
        ref="progressSection"
        class="progress-section"
        :class="{
          'inverted-colors': isLightBackground,
          'animate-in': progressAnimated,
        }"
        v-if="apartment.progress && apartment.progress >= 0"
      >
        <div class="progress-header">
          <h3 class="section-title">Avance de Obra</h3>
          <span class="progress-percent"
            >{{ progressAnimated ? apartment.progress : 0 }}%</span
          >
        </div>
        <div class="progress-bar-container">
          <div
            class="progress-bar-fill"
            :style="{
              width: (progressAnimated ? apartment.progress : 0) + '%',
            }"
          ></div>
        </div>
      </div>

      <!-- Project Features -->
      <div
        class="features-section content-section"
        style="--section-index: 2"
        v-if="features && features.length > 0"
      >
        <h3 class="section-title">Características del Proyecto</h3>
        <div class="features-grid">
          <div
            class="feature-card"
            v-for="(feature, key) in features"
            :key="key"
          >
            <div class="feature-icon">
              <img
                v-if="hasImage(feature)"
                :src="feature.urlLogoCaracteristica"
                alt=""
              />
              <font-awesome-icon
                v-else
                :icon="['fal', setIcon(feature)]"
                class="feature-fa-icon"
              />
            </div>
            <span class="feature-text">
              {{
                feature.mostrarCategoriaTitulo
                  ? feature.categoria
                  : feature.descripcion
              }}
            </span>
          </div>
        </div>
      </div>

      <!-- Project Images -->
      <div
        class="images-section content-section"
        style="--section-index: 3"
        v-if="images && images.length > 0"
      >
        <h3 class="section-title">Galería del Proyecto</h3>
        <div class="images-grid">
          <div
            v-for="(image, index) in images.slice(0, 6)"
            :key="index"
            class="image-item"
            @click="openImageModal(index)"
            :style="{ backgroundImage: 'url(' + getPhotoUrl(image) + ')' }"
          >
            <div class="image-overlay" v-if="index === 5 && images.length > 6">
              <span>+{{ images.length - 6 }}</span>
            </div>
          </div>
        </div>

        <!-- Images Modal -->
        <b-modal
          id="modal-photo2"
          size="xl"
          centered
          hide-header
          body-bg-variant="dark"
          content-class="gallery-modal"
          hide-footer
        >
          <div class="modal-gallery">
            <img
              :src="getPhotoUrl(images[setedPhoto2])"
              alt="Imagen proyecto"
            />
            <div class="modal-nav">
              <button
                class="modal-btn"
                @click="
                  setedPhoto2 =
                    (setedPhoto2 - 1 + images.length) % images.length
                "
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <span class="modal-counter"
                >{{ setedPhoto2 + 1 }} / {{ images.length }}</span
              >
              <button
                class="modal-btn"
                @click="setedPhoto2 = (setedPhoto2 + 1) % images.length"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </b-modal>
      </div>
    </div>
  </div>
</template>

<script>
import BuyProgress from "../../components/BuyProgress.vue";

export default {
  name: "showApartment",
  components: {
    BuyProgress,
  },
  props: {
    asset_id: { default: null },
    photos: { default: () => [] },
    images: { default: () => [] },
    features: { default: () => [] },
  },
  data() {
    return {
      apartment: null,
      carouselPosition: 0,
      setedPhoto: 0,
      setedPhoto2: 0,
      progressAnimated: false,
      progressObserver: null,
    };
  },
  mounted() {
    this.setApartment();
    this.setupProgressObserver();
  },
  beforeDestroy() {
    if (this.progressObserver) {
      this.progressObserver.disconnect();
    }
  },
  methods: {
    setupProgressObserver() {
      this.$nextTick(() => {
        if (this.$refs.progressSection) {
          this.progressObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting && !this.progressAnimated) {
                  // Small delay for smoother effect
                  setTimeout(() => {
                    this.progressAnimated = true;
                  }, 150);
                }
              });
            },
            {
              threshold: 0.3, // Trigger when 30% visible
              rootMargin: "0px",
            }
          );
          this.progressObserver.observe(this.$refs.progressSection);
        }
      });
    },
    setApartment() {
      if (this.asset_id) {
        this.apartment = this.$store.getters.getApartmentById(
          parseInt(this.asset_id)
        );
        console.log(
          "ShowApartmentNew - asset_id:",
          this.asset_id,
          "apartment:",
          this.apartment
        );
      }
    },
    getPhotoUrl(photo) {
      if (!photo || !photo.url) return "";
      return photo.url
        .trim()
        .replace(/ /g, "%20")
        .replace(/\(/g, "%28")
        .replace(/\)/g, "%29");
    },
    formatNumber(num) {
      return new Intl.NumberFormat("es-CL").format(num);
    },
    prevPhoto() {
      this.carouselPosition =
        (this.carouselPosition - 1 + this.photos.length) % this.photos.length;
    },
    nextPhoto() {
      this.carouselPosition = (this.carouselPosition + 1) % this.photos.length;
    },
    showPhoto() {
      this.setedPhoto = this.carouselPosition;
      this.$root.$emit("bv::show::modal", "modal-photo");
    },
    openImageModal(index) {
      this.setedPhoto2 = index;
      this.$root.$emit("bv::show::modal", "modal-photo2");
    },
    hideData(assetType) {
      return [
        "ESTACIONAMIENTO",
        "BODEGA",
        "SITIO",
        "LOTE",
        "BICICLETERO",
        "ACCION",
      ].includes(assetType);
    },
    hasImage(feature) {
      return (
        feature.urlLogoCaracteristica &&
        feature.urlLogoCaracteristica.length > 0 &&
        feature.urlLogoCaracteristica !==
          "https://s3.amazonaws.com/gestion.mobysuite/null"
      );
    },
    setIcon(feature) {
      const iconMap = {
        "Areas Verdes": "leaf",
        "Areas comunes": "dumbbell",
        Infraestructura: "building",
        "Característica departamento": "door-closed",
        "Espacios Utiles": "garage-car",
        Plusvalía: "chart-line",
      };
      return iconMap[feature.categoria] || "building";
    },
    isColorLight(color) {
      if (!color) return false;
      const hex = color.replace("#", "");
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.8;
    },
  },
  watch: {
    asset_id: {
      handler: function () {
        this.setApartment();
        this.carouselPosition = 0;
        // Reset progress animation for new property
        this.progressAnimated = false;
        this.setupProgressObserver();
      },
    },
  },
  computed: {
    customer() {
      return this.$store.getters.getCustomer;
    },
    template() {
      return this.$store.getters.getCustomerData?.template || "default";
    },
    country() {
      return this.$store.getters.getCustomerData?.country || 1;
    },
    isLightBackground() {
      const menuTextColor =
        this.$store.getters.getCustomerData?.color_menu_text;
      return this.isColorLight(menuTextColor);
    },
  },
};
</script>

<style scoped>
.property-detail-wrapper {
  min-height: 200px;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #6b7280;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: var(--color-btn-a, #3b82f6);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  margin: 0;
  font-size: 0.95rem;
}

.property-detail {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* Fade in animation for main content */
.property-detail.fade-in {
  animation: fadeInUp 0.5s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Section Titles */
.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 16px 0;
  animation: titleFadeIn 0.4s ease backwards;
}

@keyframes titleFadeIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Gallery Section */
.gallery-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: fadeSlideUp 0.5s ease backwards;
}

@keyframes fadeSlideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Content Sections Animation */
.content-section {
  animation: sectionFadeIn 0.4s ease backwards;
  animation-delay: calc(var(--section-index, 0) * 0.15s + 0.3s);
}

@keyframes sectionFadeIn {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.gallery-main {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
}

.gallery-image {
  width: 100%;
  height: 320px;
  background-size: cover;
  background-position: center;
}

.gallery-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    transparent 50%,
    rgba(0, 0, 0, 0.4) 100%
  );
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px;
}

.gallery-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
}

.gallery-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #1f2937;
}

.gallery-btn:hover {
  background: white;
  transform: scale(1.1);
}

.gallery-counter {
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

.gallery-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  color: white;
  font-size: 0.85rem;
  opacity: 0.9;
}

.gallery-thumbs {
  display: flex;
  gap: 8px;
}

.thumb-btn {
  width: 72px;
  height: 54px;
  border-radius: 8px;
  border: 2px solid transparent;
  background-size: cover;
  background-position: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  animation: thumbSlideIn 0.3s ease backwards;
}

.thumb-btn:nth-child(1) {
  animation-delay: 0.3s;
}
.thumb-btn:nth-child(2) {
  animation-delay: 0.35s;
}
.thumb-btn:nth-child(3) {
  animation-delay: 0.4s;
}
.thumb-btn:nth-child(4) {
  animation-delay: 0.45s;
}
.thumb-btn:nth-child(5) {
  animation-delay: 0.5s;
}

@keyframes thumbSlideIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.thumb-btn:hover {
  border-color: #9ca3af;
}

.thumb-btn.active {
  border-color: var(--color-btn-a, #3b82f6);
}

.thumb-more {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

/* Stats Section */
.stats-section {
  padding: 0;
  animation: sectionFadeIn 0.4s ease backwards;
  animation-delay: 0.15s;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  animation: cardSlideIn 0.4s ease backwards;
  animation-delay: calc(var(--card-index, 0) * 0.1s);
}

@keyframes cardSlideIn {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card:hover {
  border-color: #d1d5db;
  background: #f3f4f6;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-btn-a, #3b82f6);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.stat-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
}

.stat-label {
  font-size: 0.8rem;
  color: #6b7280;
}

/* Areas Section */
.areas-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}

.area-card {
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  text-align: center;
  transition: all 0.2s ease;
  animation: cardSlideIn 0.4s ease backwards;
}

.area-card:nth-child(1) {
  animation-delay: 0.2s;
}
.area-card:nth-child(2) {
  animation-delay: 0.3s;
}
.area-card:nth-child(3) {
  animation-delay: 0.4s;
}
.area-card:nth-child(4) {
  animation-delay: 0.5s;
}
.area-card:nth-child(5) {
  animation-delay: 0.6s;
}

.area-card:hover {
  border-color: #d1d5db;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.area-value {
  display: block;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 4px;
}

.area-label {
  font-size: 0.8rem;
  color: #6b7280;
}

/* Progress Section */
.progress-section {
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.progress-header .section-title {
  margin: 0;
}

.progress-percent {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-a, #3b82f6);
}

.progress-bar-container {
  height: 10px;
  background: #e5e7eb;
  border-radius: 5px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: var(--color-a, #3b82f6);
  border-radius: 5px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Progress animation on scroll */
.progress-section .progress-header {
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.4s ease;
}

.progress-section.animate-in .progress-header {
  opacity: 1;
  transform: translateY(0);
}

.progress-section .progress-percent {
  transition: all 0.3s ease;
}

/* Inverted colors for light backgrounds */
.progress-section.inverted-colors .progress-percent {
  color: var(--color-a, #3b82f6);
}

.progress-section.inverted-colors .progress-bar-fill {
  background: var(--color-a, #3b82f6);
}

/* Features Section */
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.feature-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  animation: cardSlideIn 0.4s ease backwards;
}

.feature-card:nth-child(1) {
  animation-delay: 0.3s;
}
.feature-card:nth-child(2) {
  animation-delay: 0.35s;
}
.feature-card:nth-child(3) {
  animation-delay: 0.4s;
}
.feature-card:nth-child(4) {
  animation-delay: 0.45s;
}
.feature-card:nth-child(5) {
  animation-delay: 0.5s;
}
.feature-card:nth-child(6) {
  animation-delay: 0.55s;
}
.feature-card:nth-child(7) {
  animation-delay: 0.6s;
}
.feature-card:nth-child(8) {
  animation-delay: 0.65s;
}

.feature-card:hover {
  border-color: #d1d5db;
  background: #f3f4f6;
}

.feature-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-icon img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.feature-fa-icon {
  font-size: 1.25rem;
  color: var(--color-btn-a, #3b82f6);
}

.feature-text {
  font-size: 0.9rem;
  color: #374151;
  line-height: 1.3;
}

/* Images Section */
.images-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.image-item {
  aspect-ratio: 4/3;
  background-size: cover;
  background-position: center;
  border-radius: 12px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: transform 0.2s ease;
  animation: imageScaleIn 0.4s ease backwards;
}

.image-item:nth-child(1) {
  animation-delay: 0.5s;
}
.image-item:nth-child(2) {
  animation-delay: 0.55s;
}
.image-item:nth-child(3) {
  animation-delay: 0.6s;
}
.image-item:nth-child(4) {
  animation-delay: 0.65s;
}
.image-item:nth-child(5) {
  animation-delay: 0.7s;
}
.image-item:nth-child(6) {
  animation-delay: 0.75s;
}

@keyframes imageScaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.image-item:hover {
  transform: scale(1.02);
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  font-weight: 600;
}

/* Modal Styles */
.gallery-modal {
  background: transparent !important;
  border: none !important;
}

.modal-gallery {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.modal-gallery img {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 8px;
}

.modal-nav {
  display: flex;
  align-items: center;
  gap: 24px;
}

.modal-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-counter {
  color: white;
  font-size: 1rem;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .property-detail {
    gap: 20px;
  }

  .section-title {
    font-size: 1rem;
    margin-bottom: 12px;
  }

  .gallery-image {
    height: 220px;
  }

  .gallery-btn {
    width: 36px;
    height: 36px;
  }

  .gallery-counter {
    padding: 5px 10px;
    font-size: 0.8rem;
  }

  .gallery-hint {
    font-size: 0.75rem;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .stat-card {
    padding: 14px;
    gap: 12px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
  }

  .stat-value {
    font-size: 1.35rem;
  }

  .stat-label {
    font-size: 0.75rem;
  }

  .areas-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .area-card {
    padding: 16px 12px;
  }

  .area-value {
    font-size: 1.1rem;
  }

  .area-label {
    font-size: 0.75rem;
  }

  .progress-section {
    padding: 16px;
  }

  .progress-percent {
    font-size: 1.1rem;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .feature-card {
    padding: 12px 14px;
  }

  .feature-icon {
    width: 32px;
    height: 32px;
  }

  .feature-text {
    font-size: 0.85rem;
  }

  .images-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .image-item {
    aspect-ratio: 1/1;
  }

  .modal-gallery img {
    max-height: 60vh;
  }

  .modal-nav {
    gap: 16px;
  }

  .modal-btn {
    width: 40px;
    height: 40px;
  }
}

@media (max-width: 576px) {
  .property-detail {
    gap: 16px;
  }

  .gallery-section {
    gap: 8px;
  }

  .gallery-main {
    border-radius: 12px;
  }

  .gallery-image {
    height: 200px;
  }

  .gallery-thumbs {
    display: none;
  }

  .gallery-overlay {
    padding: 12px;
  }

  .gallery-btn {
    width: 32px;
    height: 32px;
  }

  .gallery-btn svg {
    width: 16px;
    height: 16px;
  }

  .gallery-counter {
    top: 12px;
    right: 12px;
    padding: 4px 8px;
    font-size: 0.75rem;
  }

  .gallery-hint {
    display: none;
  }

  .stats-section {
    padding: 0;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .stat-card {
    padding: 12px;
    gap: 10px;
    border-radius: 10px;
  }

  .stat-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
  }

  .stat-icon svg {
    width: 20px;
    height: 20px;
  }

  .stat-value {
    font-size: 1.2rem;
  }

  .stat-label {
    font-size: 0.7rem;
  }

  .areas-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .area-card {
    padding: 14px 10px;
    border-radius: 10px;
  }

  .area-value {
    font-size: 1rem;
    margin-bottom: 2px;
  }

  .area-label {
    font-size: 0.7rem;
  }

  .progress-section {
    padding: 14px;
    border-radius: 10px;
  }

  .progress-header {
    margin-bottom: 10px;
  }

  .progress-header .section-title {
    font-size: 0.95rem;
  }

  .progress-percent {
    font-size: 1rem;
  }

  .progress-bar-container {
    height: 8px;
  }

  .features-grid {
    gap: 8px;
  }

  .feature-card {
    padding: 10px 12px;
    border-radius: 8px;
    gap: 10px;
  }

  .feature-icon {
    width: 28px;
    height: 28px;
  }

  .feature-fa-icon {
    font-size: 1rem;
  }

  .feature-text {
    font-size: 0.8rem;
  }

  .images-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .image-item {
    border-radius: 10px;
  }

  .image-overlay {
    font-size: 1rem;
  }

  .modal-gallery img {
    max-height: 50vh;
    border-radius: 6px;
  }

  .modal-nav {
    gap: 12px;
  }

  .modal-btn {
    width: 36px;
    height: 36px;
  }

  .modal-counter {
    font-size: 0.9rem;
  }
}

@media (max-width: 375px) {
  .property-detail {
    gap: 14px;
  }

  .gallery-image {
    height: 180px;
  }

  .section-title {
    font-size: 0.95rem;
  }

  .stats-grid {
    gap: 6px;
  }

  .stat-card {
    padding: 10px;
    gap: 8px;
  }

  .stat-icon {
    width: 32px;
    height: 32px;
  }

  .stat-value {
    font-size: 1.1rem;
  }

  .areas-grid {
    gap: 6px;
  }

  .area-card {
    padding: 12px 8px;
  }

  .area-value {
    font-size: 0.95rem;
  }

  .images-grid {
    gap: 6px;
  }
}
</style>
